import React, { useState, useMemo } from "react";
import { Modal, Box, Checkbox, FormControlLabel } from "@mui/material";
import "./terms-and-conditions-modal.scss";

type TermItem = {
  id: string;
  text: string;
  mandatory: boolean;
};

type TermsAndConditionsModalProps = {
  open: boolean;
  onAccept: () => void;
  loading?: boolean;
};

const TERMS: TermItem[] = [
  {
    id: "data-usage",
    text: "I acknowledge and agree that my interactions with the AI agents, including prompts, uploaded documents, generated outputs, metadata, and system usage patterns, may be logged, stored, monitored, and analyzed for purposes including service improvement, system optimization, security monitoring, audit compliance, performance evaluation, troubleshooting, and product enhancement. Such data processing will be conducted in accordance with applicable data protection regulations and organizational governance policies.",
    mandatory: true,
  },
  {
    id: "confidentiality",
    text: "I agree not to input, upload, or transmit confidential, classified, trade-secret, proprietary, or otherwise sensitive information unless I am explicitly authorized to do so and the platform environment is approved for handling such information under applicable internal security and data governance policies. Responsibility for appropriate data classification remains with the user.",
    mandatory: true,
  },
  {
    id: "ai-limitations",
    text: "I understand that AI-generated content, recommendations, assessments, translations, analyses, and summaries may contain inaccuracies, incomplete information, contextual misunderstandings, or unintended bias. I agree that all outputs must be independently reviewed, validated, and approved by qualified personnel before being relied upon for legal decisions, hiring determinations, contractual commitments, financial approvals, regulatory filings, or external communications.",
    mandatory: true,
  },
  {
    id: "compliance",
    text: "I agree to use the platform strictly in accordance with all applicable laws, regulations, industry standards, contractual obligations, and internal organizational policies, including but not limited to data protection laws, employment regulations, procurement frameworks, anti-discrimination laws, intellectual property rules, and information security requirements.",
    mandatory: true,
  },
  {
    id: "responsible-use",
    text: "I will use the AI agents in a professional, ethical, and responsible manner. I will not intentionally generate misleading, harmful, discriminatory, defamatory, unethical, unlawful, or non-compliant content, nor will I use the system in a manner that could damage the organization’s reputation, violate professional standards, or circumvent established review processes.",
    mandatory: true,
  },
  {
    id: "recruitment-decision-responsibility",
    text: "I acknowledge that outputs generated by the Candidate Screener, Skill Inference Engine, JD Composer, and AI Skill Assessment and Learning Pathway Agent are advisory and assistive tools intended to support human decision-making. Final recruitment, screening, shortlisting, interview progression, and hiring decisions remain solely the responsibility of authorized human stakeholders.",
    mandatory: true,
  },
  {
    id: "non-discrimination",
    text: "I agree that the platform will not be used to intentionally discriminate against any candidate or individual on the basis of protected characteristics under applicable employment or civil rights laws. Human oversight remains necessary to ensure fairness and equal opportunity in hiring processes.",
    mandatory: true,
  },
  {
    id: "resume-data-consent",
    text: "I confirm that I have obtained all necessary permissions, lawful basis, and organizational authorization to upload and process candidate resumes, interview feedback, assessment results, and other personal data within the platform. I acknowledge responsibility for ensuring compliance with data privacy and employment regulations.",
    mandatory: true,
  },
  {
    id: "contract-review-disclaimer",
    text: "I understand that outputs generated by the Contract Request (SOW Creator) Agent, Template Review (MSA) Agent, and Contract Review, Negotiation and Finalization Agent are AI-assisted drafts and compliance analyses. These outputs do not constitute formal legal advice, binding contractual guidance, or regulatory certification, and must be reviewed and approved by qualified legal, procurement, finance, and business authorities before execution.",
    mandatory: true,
  },
  {
    id: "procurement-and-finance-review",
    text: "I acknowledge that commercial terms, negotiation summaries, deviation reports, risk assessments, and financial analyses generated by the platform are preliminary support tools. Formal internal governance, approval workflows, and authorization hierarchies must be followed prior to entering into any contractual or financial commitment.",
    mandatory: true,
  },
  {
    id: "translation-accuracy",
    text: "I understand that translations generated by the Translator Agent, including general, corporate, and legal translations, may require linguistic validation and, where applicable, certified legal review prior to official submission, publication, regulatory filing, or contractual use.",
    mandatory: true,
  },
  {
    id: "media-and-communications-review",
    text: "I acknowledge that press releases, media Q&A briefs, corporate communications emails, and social media content generated by the Press Release and Corporate Communications Agent, Press Release and Media Brief Agent, and Social Media Agent must undergo established brand, communications, legal, and executive review processes prior to publication or external distribution.",
    mandatory: true,
  },
  {
    id: "social-media-liability",
    text: "I accept full responsibility for all externally published communications derived from AI-generated outputs, including content prepared for LinkedIn, Instagram, and X. Publication decisions and reputational implications remain the responsibility of the organization and its authorized representatives.",
    mandatory: true,
  },
  {
    id: "skill-assessment-disclaimer",
    text: "I understand that assessments conducted by the AI Skill Assessment and Learning Pathway Agent, including self-declarations, interview-based evaluations, gap analyses, and training recommendations, are indicative and developmental in nature. They do not constitute formal certification, performance appraisal, or official qualification endorsement unless independently validated through approved organizational processes.",
    mandatory: true,
  },
  {
    id: "intellectual-property",
    text: "I acknowledge that ownership, licensing rights, derivative works, and usage permissions related to AI-generated content are governed by applicable intellectual property laws, contractual agreements, and internal organizational policies. Users are responsible for ensuring that generated content does not infringe third-party intellectual property rights.",
    mandatory: true,
  },
  {
    id: "no-system-abuse",
    text: "I will not attempt to reverse engineer, deconstruct, manipulate, probe, overload, scrape, extract system prompts from, or otherwise exploit the AI models, workflows, evaluation mechanisms, or underlying infrastructure for unauthorized purposes.",
    mandatory: true,
  },
  {
    id: "security-compliance",
    text: "I agree not to upload malicious code, harmful scripts, corrupted files, unauthorized data, or any material that may compromise the confidentiality, integrity, or availability of the platform or connected systems.",
    mandatory: true,
  },
  {
    id: "service-availability",
    text: "I acknowledge that platform performance, AI model responses, database connectivity, integrations, and APIs may be subject to latency, maintenance windows, technical limitations, upgrades, or unforeseen service interruptions. No uninterrupted availability is guaranteed.",
    mandatory: true,
  },
  {
    id: "third-party-integrations",
    text: "I acknowledge that certain functionalities may rely on third-party data sources, tools, APIs, or infrastructure services. Their availability, performance, accuracy, and compliance are subject to the respective third-party providers.",
    mandatory: true,
  },
  {
    id: "feature-modifications",
    text: "I understand that platform features, agent capabilities, evaluation logic, templates, scoring methodologies, workflows, and interface components may be enhanced, modified, restricted, or discontinued at the provider’s discretion as part of continuous improvement and product evolution.",
    mandatory: true,
  },
  {
    id: "output-retention",
    text: "I understand that user inputs, generated outputs, evaluation results, and related metadata may be retained in accordance with organizational data retention policies, audit requirements, regulatory compliance obligations, and security monitoring standards.",
    mandatory: true,
  },
  {
    id: "sovereign-version-availability",
    text: "I understand that the AI Skill Assessment and Learning Pathway Agent and the Interviewer Host are currently operating within the standard deployment environment and are not presently available in a dedicated Sovereign version. A Sovereign version of these agents is under active development and is intended for release in a future platform update. Until the Sovereign version is formally launched, all functionalities, data processing activities, and operational workflows associated with these agents will continue to operate within the existing platform infrastructure. Any reference to 'Sovereign version coming soon' reflects a planned enhancement roadmap and does not constitute a currently available feature, contractual guarantee, or commitment to immediate deployment. Timelines, scope, technical architecture, and feature sets associated with the Sovereign version may evolve or change at the platform provider’s discretion. By continuing to use these agents, I confirm that I understand and accept that the Sovereign version is an upcoming capability and is not currently deployed.",
    mandatory: true,
  },
  {
    id: "updates",
    text: "I consent to receive notifications, announcements, and informational communications regarding new features, agent enhancements, platform improvements, and related product updates.",
    mandatory: false,
  },
  {
    id: "feedback",
    text: "I agree to optionally participate in surveys, feedback initiatives, or structured improvement programs intended to enhance platform usability, performance, and user experience.",
    mandatory: false,
  },
];

export default function TermsAndConditionsModal({
  open,
  onAccept,
  loading = false,
}: TermsAndConditionsModalProps): JSX.Element {
  const [acceptedAll, setAcceptedAll] = useState<boolean>(false);

  const mandatoryTerms = useMemo(
    () => TERMS.filter((term) => term.mandatory),
    [],
  );

  const optionalTerms = useMemo(
    () => TERMS.filter((term) => !term.mandatory),
    [],
  );

  const handleAcceptAllToggle = () => {
    setAcceptedAll((prev) => !prev);
  };

  const handleAccept = () => {
    if (acceptedAll && !loading) {
      onAccept();
    }
  };

  return (
    <Modal
      open={open}
      className="terms-modal"
      aria-labelledby="terms-modal-title"
      aria-describedby="terms-modal-description"
      disableEscapeKeyDown
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgba(0, 0, 0, 0.8)" },
        },
      }}
    >
      <Box className="terms-modal-content">
        <div className="terms-modal-header">
          <h2 className="terms-modal-title" id="terms-modal-title">
            Terms and Conditions
          </h2>
          <p className="terms-modal-subtitle">
            Please review and accept the following terms to access the platform
          </p>
        </div>

        <div className="terms-modal-body">
          <div className="terms-section">
            <div className="terms-section-header">
              <h3 className="terms-section-title">Required Terms</h3>
            </div>
            <div className="terms-list">
              {mandatoryTerms.map((term, index) => (
                <div key={term.id} className="term-item">
                  <span className="term-text">
                    {index + 1}. {term.text}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {optionalTerms.length > 0 && (
            <div className="terms-section">
              <div className="terms-section-header">
                <h3 className="terms-section-title">Optional Terms</h3>
              </div>
              <div className="terms-list">
                {optionalTerms.map((term, index) => (
                  <div key={term.id} className="term-item">
                    <span className="term-text">
                      {mandatoryTerms.length + index + 1}. {term.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="terms-accept-all">
            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptedAll}
                  onChange={handleAcceptAllToggle}
                  sx={{
                    color: "#1e6b59",
                    "&.Mui-checked": {
                      color: "#1e6b59",
                    },
                  }}
                />
              }
              label={
                <span style={{ fontWeight: 600, fontSize: "15px" }}>
                  I accept all terms and conditions
                </span>
              }
            />
          </div>
        </div>

        <div className="terms-modal-footer">
          <button
            className={`terms-modal-button accept ${!acceptedAll || loading ? "disabled" : ""}`}
            onClick={handleAccept}
            disabled={!acceptedAll || loading}
          >
            {loading ? "Processing..." : "Accept & Continue"}
          </button>
        </div>
      </Box>
    </Modal>
  );
}
